import React, { useState, useEffect } from "react";

export default function Step2OtpVerification({
  formData,
  otp,
  setOtp,
  otpError,
  setOtpError,
  isLoading,
  onVerify,
  onResend,
  onBack,
  otpResendTimer,
}) {
  // Focus state for styling active input
  const [focusedInput, setFocusedInput] = useState(null);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Ensure only numbers are entered
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Clear OTP error when user starts entering
      if (otpError) {
        setOtpError("");
      }

      // Auto focus to next input
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  // Handle key down to allow backspace navigation
  const handleOtpKeyDown = (index, e) => {
    // Handle backspace to move to previous input
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  // Handle paste event for OTP
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").trim();

    // Ensure only numbers and exactly 6 digits
    if (/^\d{6}$/.test(pastedText)) {
      const newOtp = pastedText.split("");
      setOtp(newOtp);
      setOtpError("");
      // Focus on the last input after paste
      document.getElementById(`otp-5`).focus();
    }
  };

  // Track if OTP is complete to enable/disable verification button
  const isOtpComplete = otp.every((digit) => digit !== "");

  // Auto-focus first input on component mount
  useEffect(() => {
    const firstInput = document.getElementById("otp-0");
    if (firstInput) {
      firstInput.focus();
    }
  }, []);

  return (
    <>
      <div className="sec-heading centered">
        <div className="content-area">
          <h2
            className="title wow fadeInUp"
            style={{ fontSize: "1.75rem" }}
            data-wow-delay=".4s">
            Verify Your Email
          </h2>
        </div>
      </div>

      <div
        className="contact-form-inputs wow fadeInUp"
        data-wow-delay=".6s"
        style={{ padding: "10px" }}>
        <div className="otp-verification-form input-boxed">
          <p className="text-center mb-4">
            We've sent a 6-digit verification code to{" "}
            <strong style={{ color: "#09aff4" }}>{formData.email}</strong>.
            Please enter the code below to continue your registration.
          </p>

          <div
            className="otp-inputs d-flex justify-content-center gap-2 mb-4"
            role="group"
            aria-label="Enter 6-digit OTP">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                className="form-control text-center"
                style={{
                  width: "50px",
                  height: "55px",
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  borderColor:
                    focusedInput === index
                      ? "#09aff4"
                      : otpError
                      ? "#dc3545"
                      : "#dee2e6",
                  borderWidth: "2px",
                  boxShadow:
                    focusedInput === index
                      ? "0 0 0 0.2rem rgba(9, 175, 244, 0.25)"
                      : "none",
                  transition: "all 0.2s ease-in-out",
                }}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={index === 0 ? handleOtpPaste : undefined}
                onFocus={() => setFocusedInput(index)}
                onBlur={() => setFocusedInput(null)}
                maxLength={1}
                autoComplete="off"
                inputMode="numeric"
                aria-label={`OTP digit ${index + 1}`}
                aria-invalid={otpError ? "true" : "false"}
              />
            ))}
          </div>

          {otpError && (
            <div
              className="alert alert-danger text-center"
              role="alert"
              style={{ fontSize: "0.9rem", borderRadius: "6px" }}>
              <i className="fas fa-exclamation-circle me-2"></i>
              {otpError}
            </div>
          )}

          <div className="d-grid gap-3">
            <button
              type="button"
              className="btn-solid w-100"
              onClick={onVerify}
              disabled={isLoading || !isOtpComplete}
              aria-label="Verify OTP and continue"
              style={{
                backgroundColor: "#09aff4",
                color: "#fff",
                border: "none",
                padding: "12px",
                borderRadius: "6px",
                fontWeight: "600",
                opacity: isLoading || !isOtpComplete ? "0.7" : "1",
                transition: "all 0.3s ease",
              }}>
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"></span>
                  Verifying...
                </>
              ) : (
                "Verify & Continue"
              )}
            </button>

            <div className="text-center mt-3">
              <div className="d-flex flex-column align-items-center">
                <button
                  type="button"
                  className="btn-link"
                  onClick={onResend}
                  disabled={isLoading || otpResendTimer > 0}
                  aria-label="Resend verification code"
                  style={{
                    color: "#09aff4",
                    textDecoration: "none",
                    border: "none",
                    background: "none",
                    padding: "0",
                    opacity: isLoading || otpResendTimer > 0 ? "0.7" : "1",
                  }}>
                  {otpResendTimer > 0
                    ? `Resend code in ${otpResendTimer}s`
                    : "Resend verification code"}
                </button>

                <button
                  type="button"
                  className="btn-link mt-3"
                  onClick={onBack}
                  disabled={isLoading}
                  aria-label="Back to sign up form"
                  style={{
                    color: "#6c757d",
                    textDecoration: "none",
                    border: "none",
                    background: "none",
                    padding: "0",
                    fontSize: "0.9rem",
                  }}>
                  <i className="fas fa-chevron-left me-1"></i>
                  Back to sign up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
